import { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "../providers/AuthProvider.jsx";
import ProtectedRoute from "./ProtectedRoute.js";
import RouteFallback from "./RouteFallback.jsx";
import PrivateLayout from "../../layouts/private/PrivateLayout.jsx";
import PrivateCleanLayout from "../../layouts/private/PrivateCleanLayout.jsx";
import PublicLayout from "../../layouts/public/PublicLayout.jsx";

import LoginPage from "../../features/auth/LoginPage.jsx";

// Páginas carregadas sob demanda (code-splitting por rota): o bundle
// inicial fica restrito a layouts + login, e cada feature só é baixada
// quando o usuário navega até ela. As 6 páginas dos menus principais
// usam webpackPrefetch: o browser baixa esses chunks em idle logo após
// o load, então o primeiro clique no menu não espera rede.
const ChannelPageList = lazy(() => import(/* webpackPrefetch: true */ "../../features/channels/overview/ChannelOverviewPage.jsx"));
const ChannelPageOptions = lazy(() => import("../../features/channels/options/index.jsx"));
const ChannelIntegrationQrCodePage = lazy(() => import("../../features/channels/types/WhatsappWeb/Integration/QRCode/ChannelIntegrationQrCodePage.jsx"));
const CreateIntegrationAlcancePage = lazy(() => import("../../features/channels/types/Sms/Integration/Alcance/CreateIntegrationAlcancePage.jsx"));
const CreateIntegrationSesPage = lazy(() => import("../../features/channels/types/Email/Integration/Ses/CreateIntegrationSesPage.jsx"));
const CreateIntegrationEmbeddedPage = lazy(() => import("../../features/channels/types/WhatsappApi/Integration/EmbeddedSignup/CreateIntegrationEmbeddedPage.jsx"));
const ViewConfigChannel = lazy(() => import("../../features/channels/settings/index.js"));

const TemplatesPageList = lazy(() => import(/* webpackPrefetch: true */ "../../features/templates/overview/TemplatesOverviewPage.jsx"));
const TemplatePageAdd = lazy(() => import("../../features/templates/options/index.jsx"));
const TemplatePageChannelAdd = lazy(() => import("../../features/templates/create/index.js"));

const IntegrationsPageAdd = lazy(() => import(/* webpackPrefetch: true */ "../../features/integrations/options/index.jsx"));
const AddIntegration = lazy(() => import("../../features/integrations/AddIntegration.js"));

const PasswordRecoveryPage = lazy(() => import("../../features/auth/PasswordRecoveryPage.jsx"));
const DashboardPage = lazy(() => import(/* webpackPrefetch: true */ "../../features/dashboard/index.js"));

const CampaignCreatePage = lazy(() => import("../../features/campaigns/create/CampaignCreatePage.jsx"));
const CampaignEditPage = lazy(() => import("../../features/campaigns/edit/CampaignEditPage.jsx"));
const CampaignPage = lazy(() => import(/* webpackPrefetch: true */ "../../features/campaigns/overview/index.js"));
const CampaignViewPage = lazy(() => import("../../features/campaigns/view/CampaignViewPage.jsx"));

const Sheets = lazy(() => import(/* webpackPrefetch: true */ "../../features/sheets/overview/index.js"));
const AddSheetsOptions = lazy(() => import("../../features/sheets/options/index.jsx"));
const PageAddGoogleSheets = lazy(() => import("../../features/sheets/types/GoogleSheets/create/addGoogleSheets.jsx"));
const PageAddExcel = lazy(() => import("../../features/sheets/types/Excel/create/addExcel.jsx"));
const PageAddApiRest = lazy(() => import("../../features/sheets/types/ApiRest/create/addApiRest.jsx"));
const AddSheetsIntegrationInvoice = lazy(() => import("../../features/sheets/types/Erps/create/addSheetsIntegrationInvoice.js"));

const ProfileSetupPage = lazy(() => import("../../features/profile/ProfileSetupPage.jsx"));
const SignUp = lazy(() => import("../../features/auth/SignupPage.jsx"));
const VerifyEmailPage = lazy(() => import("../../features/auth/VerifyEmailPage.jsx"));

const NotFound = lazy(() => import("../../shared/components/common/ui/NotFound/index.jsx"));
const SessionExpired = lazy(() => import("../../shared/components/common/ui/SessionExpired/index.jsx"));

const SupportAccessPage = lazy(() => import("../../features/auth/SupportAccessPage.jsx"));
const ResetRequiredPasswordPage = lazy(() => import("../../features/auth/ResetRequiredPasswordPage.jsx"));

function RootRedirect() {
  const { isAuthenticated, status } = useAuth();

  if (status === "booting") return null;

  return isAuthenticated
    ? <Navigate to="/painel" replace />
    : <Navigate to="/login" replace />;
}

function AppRoutes() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>

        {/* ================= ROOT ================= */}

        <Route element={<PublicLayout />}>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/esqueci-senha" element={<PasswordRecoveryPage />} />
          <Route path="/cadastro" element={<SignUp />} />
          <Route path="/support-login" element={<SupportAccessPage />} />
          <Route path="/reset-senha" element={<ResetRequiredPasswordPage />} />
          <Route path="/logout" element={<SessionExpired />} />
        </Route>

        {/* ================= PROTECTED ================= */}

        <Route element={<ProtectedRoute />}>
          <Route element={<PrivateCleanLayout />}>
            <Route path="/verificar-email" element={<VerifyEmailPage />} />
          </Route>

          <Route element={<PrivateLayout />}>

            <Route path="/painel" element={<DashboardPage />} />
            <Route path="/perfil" element={<ProfileSetupPage />} />

            <Route path="/canais" element={<ChannelPageList />} />
            <Route path="/canais/adicionar" element={<ChannelPageOptions />} />
            <Route path="/canais/adicionar/whatsapp-web" element={<ChannelIntegrationQrCodePage />} />
            <Route path="/canais/adicionar/whatsapp-api" element={<CreateIntegrationEmbeddedPage />} />
            <Route path="/canais/adicionar/sms" element={<CreateIntegrationAlcancePage />} />
            <Route path="/canais/adicionar/email" element={<CreateIntegrationSesPage />} />
            <Route path="/canais/configurar" element={<ViewConfigChannel />} />

            <Route path="/modelos" element={<TemplatesPageList />} />
            <Route path="/modelos/canal" element={<TemplatePageAdd />} />
            <Route path="/modelos/canal/whatsapp-web" element={<TemplatePageChannelAdd channel="whatsapp_web" />} />
            <Route path="/modelos/canal/whatsapp-api" element={<TemplatePageChannelAdd channel="whatsapp_api" />} />
            <Route path="/modelos/canal/email" element={<TemplatePageChannelAdd channel="email" />} />
            <Route path="/modelos/canal/sms" element={<TemplatePageChannelAdd channel="sms" />} />

            <Route path="/listagens" element={<Sheets />} />
            <Route path="/listagens/adicionar" element={<AddSheetsOptions />} />
            <Route path="/listagens/adicionar/google" element={<PageAddGoogleSheets />} />
            <Route path="/listagens/adicionar/api" element={<PageAddApiRest />} />
            <Route path="/listagens/adicionar/excel" element={<PageAddExcel />} />
            <Route path="/listagens/adicionar/:integration" element={<AddSheetsIntegrationInvoice />} />
            <Route path="/listagens/adicionar/:integration/faturas em aberto" element={<AddSheetsIntegrationInvoice />} />

            <Route path="/campanhas" element={<CampaignPage />} />
            <Route path="/campanhas/adicionar" element={<CampaignCreatePage />} />
            <Route path="/campanhas/:id/editar" element={<CampaignEditPage />} />
            <Route path="/campanhas/:id" element={<CampaignViewPage />} />

            <Route path="/integrações" element={<IntegrationsPageAdd />} />
            <Route path="/integrações/adicionar" element={<AddIntegration />} />

          </Route>
        </Route>

        {/* ================= FALLBACK ================= */}

        <Route path="*" element={<NotFound />} />

      </Routes>
    </Suspense>
  );
}

export default AppRoutes;
